import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, Button } from '@mui/material';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import { renderField } from 'components/utils';
import Modal from 'components/common/Modal';
import { FormTextField, FormCustom, ColorField, normalizeColorToHex } from 'components/common/form';
import { IStatus } from 'components/settings/CommonStatusList';
import { useEntitySimpleFieldAutosave, AutoSaveStatusIndicator } from 'hooks/autoSave';

type FormData = {
  label: string;
  color: string;
};

const schema = yup
  .object({
    label: yup.string().required('Label is required'),
    color: yup.string().required('Color is required'),
  })
  .required();

const getAutosaveFormSnapshot = (values: FormData) => ({
  label: values.label || '',
  color: values.color || '',
});

const getAutosaveValidationFields = (changedKeys: string[]) => {
  const fields: string[] = [];

  for (const key of changedKeys) {
    if (key === 'label' || key === 'color') {
      fields.push(key);
    }
  }

  return fields;
};

type Props = {
  endpoint: string;
  onClose: () => void;
  onSuccess: (status?: IStatus) => void;
  open: boolean;
  editingField?: string;
  status?: IStatus;
};

const StatusForm = ({ endpoint, open, onClose, onSuccess, status, editingField }: Props) => {
  const statusO = status || { _id: null, label: '', color: '#000000', name: '' };
  const isUpdate = Boolean(statusO._id);

  const [saving, setSaving] = useState(false);
  const [formReady, setFormReady] = useState(false);

  const { control, handleSubmit, setValue, clearErrors, watch, reset, getValues, trigger } = useForm<
    FormData,
    unknown,
    yup.InferType<typeof schema>
  >({
    resolver: yupResolver(schema),
    defaultValues: {
      label: statusO.label || statusO.name,
      color: normalizeColorToHex(statusO.color || '#000000'),
    },
  });

  useEffect(() => {
    if (!open) {
      setFormReady(false);
      return;
    }

    const current = status || { label: '', name: '', color: '#000000' };

    reset({
      label: current.label || current.name || '',
      color: normalizeColorToHex(current.color || '#000000'),
    });
    setFormReady(Boolean(status?._id));
  }, [open, status, reset]);

  const formValues = watch();
  const serializedValues = JSON.stringify(getAutosaveFormSnapshot(formValues));

  const seedSnapshot = useCallback(
    () => JSON.stringify(getAutosaveFormSnapshot(getValues())),
    [getValues],
  );

  const getValidationFields = useCallback(
    (changedKeys: string[]) => getAutosaveValidationFields(changedKeys),
    [],
  );

  const updateAutosave = useEntitySimpleFieldAutosave({
    enabled: isUpdate,
    isReady: formReady,
    snapshot: serializedValues,
    seedSnapshot,
    isBlocked: () => saving,
    getValidationFields,
    trigger,
    updatePath: `${endpoint}/update-simple-field`,
    idField: 'statusId',
    entityId: statusO._id || undefined,
  });

  const onSave = async (data: yup.InferType<typeof schema>) => {
    const { label, color } = data;

    setSaving(true);

    try {
      const response = (await sendApiRequest({
        path: `${endpoint}/create`,
        method: 'POST',
        data: {
          ...data,
          name: label,
          color,
        },
      })) as IStatus;

      onSuccess(response);
      notify('Status saved successfully');

      setSaving(false);
    } catch (error) {
      setSaving(false);
      notify(error);
    }
  };

  const handleClose = () => {
    if (isUpdate && updateAutosave.lastAutoSave) {
      onSuccess();
      return;
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onSubmit={
        isUpdate
          ? undefined
          : e => {
              e.preventDefault();
              e.stopPropagation();
              handleSubmit(onSave)(e);
            }
      }
      onClose={handleClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <span>{isUpdate ? 'Edit Status' : 'Add new Status'}</span>
          {isUpdate ? (
            <AutoSaveStatusIndicator
              status={updateAutosave.status}
              lastAutoSave={updateAutosave.lastAutoSave}
              type="status"
            />
          ) : null}
        </Box>
      }
      size="xs"
      renderContent={() => (
        <>
          <Box sx={editingField ? { display: editingField === 'label' ? 'block' : 'none' } : {}}>
            {renderField({
              id: 'status_form_name',
              label: 'Name',
              required: true,
              renderControl: () => {
                return <FormTextField name="label" control={control} fullWidth />;
              },
            })}
          </Box>

          <Box sx={editingField ? { display: editingField === 'color' ? 'block' : 'none' } : {}}>
            {renderField({
              id: 'status_form_color',
              label: 'Color',
              required: true,
              renderControl: () => (
                <FormCustom
                  name="color"
                  control={control}
                  renderControl={() => (
                    <ColorField
                      value={watch('color')}
                      onChange={next => {
                        setValue('color', next);
                        clearErrors('color');
                      }}
                    />
                  )}
                />
              ),
            })}
          </Box>
        </>
      )}
      renderActions={() =>
        isUpdate ? null : (
          <Button variant="contained" color="primary" type="submit" disabled={saving}>
            Add
          </Button>
        )
      }
    />
  );
};

export default StatusForm;
